import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconCheck } from '@tabler/icons-react';
import { Text } from '@mantine/core';

type ApiCallback<T> = (data: T) => void;
type ApiErrorCallback = (error: string) => void;

interface EntityManagerOptions<T> {
  fetchEntities: (search: string, onSuccess: ApiCallback<{ data: T[] }>, onError: ApiErrorCallback) => void;
  deleteEntity: (id: number, onSuccess: () => void, onError: ApiErrorCallback) => void;
  entityName: string;
  article: 'eliminado' | 'eliminada';
  filterKey?: keyof T;
}

export const useEntityManager = <T extends { id: number; name: string }>(
  options: EntityManagerOptions<T>
) => {
  const { fetchEntities, deleteEntity, entityName, article } = options;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [entities, setEntities] = useState<T[]>([]);
  const [search, setSearch] = useState<string>('');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editItem, setEditItem] = useState<T | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  // On load
  useEffect(() => {
    getEntitiesList();
    // eslint-disable-next-line
  }, []);


  // Get entities
  const getEntitiesList = () => {
    fetchEntities(
      '',
      (response) => {
        setEntities(response.data);
        setIsLoading(false);
      },
      (error) => {
        // Remove loader
        setIsLoading(false);

        // Show error
        notifications.show({
          title: 'Error',
          message: error,
          color: 'red',
        });
      }
    );
  };

  // Handle search change
  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const filteredEntities = entities.filter((item) => {
    const field = options.filterKey ?? 'name';
    const value = item[field];
    return typeof value === 'string' && value.toLowerCase().includes(search.toLowerCase())
  });

  // Close entities modal
  const handleCloseModal = () => {
    getEntitiesList();
  };

  // Toggle partner edit
  const toggleEdit = (event: React.MouseEvent, item: T) => {
    // Stop click and toggle edit
    event.stopPropagation();
    setIsEdit(true);
    setEditItem(item);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id: number) => {
    modals.openConfirmModal({
      title: `Eliminar ${entityName}`,
      centered: true,
      children: (
        <Text size="sm">¿Estás seguro de querer borrar este {entityName.toLowerCase()}? Esta acción no se puede deshacer.</Text>
      ),
      labels: { confirm: `Eliminar ${entityName}`, cancel: 'Cancelar' },
      confirmProps: { color: 'red', variant: 'light' },
      onCancel: () => { },  // Nothing
      onConfirm: () => triggerDelete(id),
      closeOnClickOutside: false,
      zIndex: 400,
    });
  };

  // Trigger delete entity
  const triggerDelete = (id: number) => {

    // Set loader
    setIsDeleteLoading(true);

    // Call API
    deleteEntity(
      id,
      () => {
        // Show success
        notifications.show({
          title: `${entityName} ${article} correctamente`,
          color: 'green',
          icon: <IconCheck />,
          message: '',
        });

        // Close after 1 second
        setTimeout(() => {

          // Remove preloader
          setIsDeleteLoading(false);

          // Close modal
          handleCloseModal();
          setIsEdit(false);
          setEditItem(null);
        }, 1000);
      },
      (error) => {

        // Remove preloader
        setIsDeleteLoading(false);

        // Show error
        notifications.show({
          title: 'Error',
          message: error,
          color: 'red',
        });
      }
    );
  };


  return {
    isLoading,
    search,
    handleSearchChange,
    filteredEntities,
    isEdit,
    setIsEdit,
    editItem,
    toggleEdit,
    handleCloseModal,
    openDeleteModal,
    isDeleteLoading,
  };
};
